import { calendarDateToLocal } from '../../../utils/calendarDate';
import React, { useMemo } from 'react';
import { AlertTriangle, ClipboardX, Clock, ClipboardCheck, Layers, Server, ShieldAlert, TrendingUp, XCircle } from 'lucide-react';
import { TestSummaryTable } from './TestSummaryTable.tsx';
import { FailingControlsList } from './FailingControlsList.tsx';
import { ExceptionRateChart } from './ExceptionRateChart.tsx';
import { SampleSizeTable } from './SampleSizeTable.tsx';
import { RollforwardTable } from './RollforwardTable.tsx';
import { IPEStatusPanel } from './IPEStatusPanel.tsx';
import { ToDToEChart } from './ToDToEChart.tsx';
import { SoDVerifiedTable } from './SoDVerifiedTable.tsx';
import { EvidenceCoverageBars } from './EvidenceCoverageBars.tsx';
import { ScopeControlsByRiskChart } from '../scope/ScopeControlsByRiskChart.tsx';
import { PageHeader, SectionHeader } from './shared.tsx';
import { useControllerTestSummary } from '../../../hooks/analytics/useControllerReports.ts';
import {
  useScopeControlCounts,
  useScopeOverdueSummary,
  useScopeSystems,
} from '../../../hooks/analytics/useScopeReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, monthBucket, monthLabel, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['period', 'workflow_status', 'conclusion'];

/**
 * Controller persona analytics dashboard (Feature 5.3, reports #20-#28).
 *
 * IA: PageHeader -> slicer bar -> always-visible KPI row -> tabbed body
 * (Overview charts / Analysis / Raw data tables). The slicer bar and KPI row
 * are driven off the Control Test Summary rows (report #20) — the persona's
 * richest per-control dataset. `test_summary` has no `cycle`/`process`
 * column, so "Testing Period" (bucketed from `testing_period_start`) stands
 * in for the Cycle slicer while Workflow Status / Conclusion cover Status.
 *
 * The resulting filtered control-id set is threaded into every other widget
 * on the page as `controlIdFilter` so the whole page — not just the summary
 * table — responds to the slicer bar. Each widget still owns its own React
 * Query fetch, so a slow/failing report never blocks the rest of the page.
 */
export const ControllerDashboard: React.FC = () => {
  const { data: summaryData, isLoading: summaryLoading } = useControllerTestSummary();
  const summaryRows = useMemo(() => summaryData ?? [], [summaryData]);

  // Scope & Coverage (feedback round 2026-08) — recomposes Controller onto
  // the same CAE-like KPI-band pattern, reusing the shared scope hooks.
  const { data: controlCountsData } = useScopeControlCounts();
  const { data: systemsData } = useScopeSystems();
  const { data: overdueData } = useScopeOverdueSummary();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      {
        key: 'period',
        label: 'Testing Period',
        options: deriveSlicerOptions(summaryRows, (r) => monthBucket(r.testing_period_start), monthLabel),
      },
      {
        key: 'workflow_status',
        label: 'Workflow Status',
        options: deriveSlicerOptions(summaryRows, (r) => r.workflow_status),
      },
      { key: 'conclusion', label: 'Conclusion', options: deriveSlicerOptions(summaryRows, (r) => r.conclusion) },
    ],
    [summaryRows]
  );

  const filteredSummaryRows = useMemo(
    () =>
      applySlicers(summaryRows, slicers.values, {
        period: (r) => monthBucket(r.testing_period_start),
        workflow_status: (r) => r.workflow_status,
        conclusion: (r) => r.conclusion,
      }),
    [summaryRows, slicers.values]
  );

  const controlIdFilter = useMemo(
    () => new Set(filteredSummaryRows.map((r) => r.control_id)),
    [filteredSummaryRows]
  );

  const kpis = useMemo<KpiItem[]>(() => {
    const tested = filteredSummaryRows.filter((r) => r.conclusion != null);
    const passed = tested.filter((r) => r.conclusion === 'effective').length;
    const failing = tested.filter((r) => r.conclusion === 'ineffective').length;
    const withExceptions = filteredSummaryRows.filter((r) => (r.total_exceptions ?? 0) > 0).length;
    const now = new Date();
    const overdue = filteredSummaryRows.filter(
      (r) => r.workflow_status !== 'completed' && r.testing_period_end && (calendarDateToLocal(r.testing_period_end) ?? now) < now
    ).length;

    return [
      {
        key: 'tested',
        title: 'Controls Tested',
        value: tested.length,
        icon: <ClipboardCheck className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
        subtitle: `of ${filteredSummaryRows.length} in view`,
      },
      {
        key: 'pass_rate',
        title: 'Pass Rate',
        value: tested.length > 0 ? `${Math.round((passed / tested.length) * 100)}%` : '—',
        icon: <TrendingUp className="h-5 w-5" />,
        color: 'bg-emerald-100 text-emerald-600',
      },
      {
        key: 'exception_rate',
        title: 'Exception Rate',
        value: tested.length > 0 ? `${Math.round((withExceptions / tested.length) * 100)}%` : '—',
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-amber-100 text-amber-600',
      },
      {
        key: 'failing',
        title: 'Failing Controls',
        value: failing,
        icon: <XCircle className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
      },
      {
        // Renamed from "Overdue Tests" (feedback round 2026-08-23): it sat on
        // the same KPI band as the "Testing Overdue" tile below (a different
        // number, from `overdueData.testing_overdue`/past-due-date), which
        // read as two contradictory overdue counts. This tile counts runs
        // whose *testing period* has ended without a completed run — a
        // distinct, unambiguous concept from "past its due date".
        key: 'overdue',
        title: 'Past Testing Period',
        value: overdue,
        icon: <Clock className="h-5 w-5" />,
        color: 'bg-orange-100 text-orange-600',
        subtitle: 'period ended, run not completed',
      },
    ];
  }, [filteredSummaryRows]);

  /**
   * Scope & Coverage KPI band (feedback round 2026-08) — visual parity with
   * the CAE dashboard's recomposed band: control counts by risk-level scope,
   * key/non-key split, systems in scope, and overdue testing/PBC.
   */
  const scopeKpis = useMemo<KpiItem[]>(
    () => [
      {
        key: 'controls_in_scope',
        title: 'Controls In Scope',
        value: controlCountsData?.total ?? 0,
        icon: <Layers className="h-5 w-5" />,
        color: 'bg-purple-100 text-purple-600',
        delta: controlCountsData?.delta ?? undefined,
        deltaSuffix: '',
        deltaDigits: 0,
      },
      { key: 'key_controls', title: 'Key Controls', value: controlCountsData?.key_count ?? 0, color: 'bg-blue-100 text-blue-600' },
      { key: 'non_key_controls', title: 'Non-Key Controls', value: controlCountsData?.non_key_count ?? 0, color: 'bg-slate-100 text-slate-600' },
      { key: 'systems', title: 'Systems In Scope', value: systemsData?.total ?? 0, icon: <Server className="h-5 w-5" />, color: 'bg-cyan-100 text-cyan-600' },
      {
        key: 'testing_overdue',
        title: 'Testing Overdue',
        value: overdueData?.testing_overdue ?? 0,
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'past due date',
      },
      {
        // Urgency-color fix (feedback round 2026-08-23): overdue is always
        // red, never amber — amber is reserved for pending/at-risk-but-not-
        // late states.
        key: 'pbc_overdue',
        title: 'PBC Overdue',
        value: overdueData?.pbc_overdue ?? 0,
        icon: <ClipboardX className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
      },
    ],
    [controlCountsData, systemsData, overdueData]
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<ClipboardCheck className="h-5 w-5" />}
          iconClassName="bg-blue-50 text-blue-600"
          title="Controller Dashboard"
          description="Control testing status, exceptions, and evidence coverage across the current cycle"
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />
        <KpiRow items={scopeKpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              icon: <ShieldAlert className="h-4 w-4" />,
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FailingControlsList controlIdFilter={controlIdFilter} />
                    <ExceptionRateChart controlIdFilter={controlIdFilter} />
                  </div>
                  <ToDToEChart controlIdFilter={controlIdFilter} />
                  <ScopeControlsByRiskChart />
                </div>
              ),
            },
            {
              key: 'analysis',
              label: 'Analysis',
              content: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <IPEStatusPanel controlIdFilter={controlIdFilter} />
                  <EvidenceCoverageBars controlIdFilter={controlIdFilter} />
                </div>
              ),
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Control Test Summary" />
                    <TestSummaryTable rows={summaryLoading ? undefined : filteredSummaryRows} />
                  </section>
                  <section>
                    <SectionHeader title="Sample Size Report" />
                    <SampleSizeTable controlIdFilter={controlIdFilter} />
                  </section>
                  <section>
                    <SectionHeader title="Re-performance & Roll-forward Tracker" />
                    <RollforwardTable controlIdFilter={controlIdFilter} />
                  </section>
                  <section>
                    <SectionHeader title="Segregation of Duties Verified" />
                    <SoDVerifiedTable controlIdFilter={controlIdFilter} />
                  </section>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ControllerDashboard;
